/*
 * File: /src/services/queueService.js
 * Created Date: Sunday May 23rd 2021
 * Author: Nicolas Wegener, Roy Collins
 * -----
 * Last Modified: Sunday, 23rd May 2021 11:03:48 pm
 * Modified By: Nicolas Wegener (wegenern@gmail.com)
 * -----
 * Copyright (c) 2021 AdBase, Inc.
 */
import { h } from 'vue';
import { authedAxios } from '../shared/axiosWrapper';
import { AD_BASE_QUEUE_PATH } from '../constants';
import globals from 'src/main';
import store from 'src/store/index';
import router from 'src/router/index';
import userNotificationService from 'src/services/userNotificationService.js';

// only load audio one time into memory when needed but config is not going
// to be available when this file is loaded but will be when its called
let notifyAudio = undefined; // For default browser notifications currently
const NOTIFY_URL =
  'https://adbase-static.s3.amazonaws.com/sounds/alert_glassy_mallet_002.mp3';
let queueCallAudio = undefined;
const QUEUE_CALL_URL =
  'https://adbase-static.s3.amazonaws.com/sounds/alert_chime_short.mp3';
// Sounds for in app notifications. Eventually dynamic for browser too
let NotificationSounds = {
  airbnb: undefined,
  email: undefined,
  lead: undefined,
  sms: undefined,
  task: undefined,
  voicemail: undefined,
  whatsapp: undefined,
};

let wsHost;
if (window.s2Config && window.s2Config.VUE_APP_WS_HOST) {
  wsHost = window.s2Config.VUE_APP_WS_HOST;
} else if (process.env.VUE_APP_WS_HOST) {
  wsHost = process.env.VUE_APP_WS_HOST;
}
if (!wsHost) {
  console.error('NO WS HOST SPECIFIED FROM CONFIG');
}

// TODO: Make this a config part too
const wsPath = 'connect/';

// Port of the shared worker that owns the WS. Set by the TopNavbar once the
// worker is started so any component can push events up the socket
let workerPort = undefined;

export default {
  /**
   * Register connection to make active
   *
   * @param {*} userId
   * @param {*} topics
   * @returns
   */
  connect(userId, topics, brands) {
    console.log(wsHost + wsPath);
    return authedAxios.post(wsHost + wsPath, {
      user_id: userId,
      topics: topics,
    });
  },

  /**
   * Disconnect from WebSocket
   *
   * @param {*} companyId
   * @param {*} uuid
   * @returns
   */
  disconnect(companyId, uuid) {
    return authedAxios.delete(wsHost + wsPath + companyId + '/' + uuid);
  },

  /**
   * Register the shared worker port used to send messages up the WS
   *
   * @param {MessagePort|undefined} port
   */
  setWorkerPort(port) {
    workerPort = port;
  },

  /**
   * Send an event up the WS. The server expects a bare payload tagged with
   * `type` (ie: conversationEvent) and only accepts whitelisted types.
   * Best effort, silently dropped if there is no socket.
   *
   * @param {string} type payload type
   * @param {object} payload
   * @returns {boolean} true if handed to the worker
   */
  send(type, payload) {
    if (!workerPort) {
      return false;
    }
    workerPort.postMessage({
      source: 'wsSend',
      data: { ...payload, type: type },
    });
    return true;
  },

  /**
   * Let other agents know this user is doing something on a conversation
   *
   * @param {number} conversationId
   * @param {string} eventType ie: 'typing'
   * @returns {boolean} true if handed to the worker
   */
  sendConversationEvent(conversationId, eventType) {
    const account = store.getters['auth/getLoginAccount'];
    if (!account || !conversationId) {
      return false;
    }
    return this.send('conversationEvent', {
      conversation_id: conversationId,
      company_id: account.companyId,
      user_id: account.userId,
      event_type: eventType,
      created_at: new Date().toISOString(),
    });
  },

  processMessage(m) {
    // We just get the raw payload from the shared worker
    const msg = JSON.parse(m);
    if (msg.topic == undefined) {
      console.error('Received an unexpected event structure', msg);
      return;
    }

    // These are seen in all case blocks, overload as needed
    let config = undefined;
    const userId = store.getters['auth/getLoginAccount'].userId;
    const brandIds = store.getters['auth/getGlobalBrandIds'];
    // TODO: We might want to filter a msg anytime the brandIds is set.
    // If there is no msg.brand_id we might want to filter all those messages
    // as they dont technically meet the filter set.  Closed vs open filter.
    // can only filter non call/agent/disposition to prevent losing state updates
    const requiredMessages = [
      'agentStatus',
      'callData',
      'disposition',
      'voice',
      'voiceSegment',
    ];
    if (
      brandIds &&
      msg.payload.brand_id &&
      !brandIds.includes(msg.payload.brand_id) &&
      !requiredMessages.includes(msg.topic)
    ) {
      // Only filter if brandIds if set. Undefined means allow all
      console.log('skipping notification due to brand');
      return;
    }

    switch (msg.topic) {
      case 'agentStatus':
        store.dispatch('realtime/modifyAgent', msg.payload);

        //Special case where OnCall list the dispId so we can route to a new call
        if (
          msg.payload.state == 'busy' &&
          msg.payload.status == 'on_call' &&
          msg.payload.user_id &&
          msg.payload.user_id == userId &&
          msg.payload.disposition_id
        ) {
          const disp = store.getters['realtime/getDispositionById'](
            msg.payload.disposition_id
          );
          if (disp) {
            store.dispatch(
              'realtime/modifyActiveDisposition',
              msg.payload.disposition_id
            );
          }
        }

        break;
      case 'callData':
        // Where is user_id stored for the current agent??
        store.dispatch('realtime/addCallData', msg.payload);
        if (msg.payload.user_id == userId) {
          // Do we even need to throw this to vuex?
        }
        break;
      case 'conversation':
        // Agent events (typing etc) share the conversation topic but are
        // not conversation updates, so split them out by payload type
        if (msg.payload.type == 'conversationEvent') {
          // We dont care about our own events
          if (msg.payload.user_id != userId) {
            store.dispatch(
              'realtime/modifyLatestConversationEvent',
              msg.payload
            );
          }
          break;
        }
        // Always to everyone
        store.dispatch('realtime/modifyLatestConversation', msg.payload);
        break;
      case 'contactTimeline':
        store.dispatch('realtime/modifyLatestContactTimeline', msg.payload);
        break;
      case 'lead':
        // console.log('we got a lead message', msg.payload);
        // store.dispatch('realtime/modifyHaveLead', true);
        break;
      case 'disposition':
        store.dispatch('realtime/modifyDisposition', msg.payload);
        break;
      case 'sms':
        // Ignore our own messages, unless its assignment
        if (msg.payload.user_id == userId && !msg.payload.is_assignment) {
          // Ignore our own messages
          break;
        }
        // Alert components we have a new sms
        store.dispatch(
          'realtime/modifyNewSmsContactId',
          msg.payload.contact_id
        );
        config = store.getters['appConfig/getAppConfig'];
        if (!config || !config.callCenter) {
          // If we cant tell just modify the badge and no notifications
          store.dispatch('realtime/modifyHaveNewSms', true);
          break;
        }

        if (msg.payload.user_id && msg.payload.is_assignment === false) {
          // Outbound non assignment messages we can skip for now.
          // Eventually a config based on direction too
          break;
        }

        // New sms badge takes takes precedence over assign
        if (msg.payload.is_assignment === false) {
          store.dispatch('realtime/modifyHaveNewSms', true);
          // process notification types
          const longBody = `From: ${msg.payload.contact_name}

${msg.payload.content}
`;
          if (config.callCenter.browser && config.callCenter.browser.smsNew) {
            const payload = {
              body: longBody,
            };
            // TODO: just make all play sounds for now.
            _makeNotification('New Sms Message', payload, 'sms', userId);
          }

          if (config.callCenter.smsNotifyNew === true) {
            let smsMessage = 'New SMS from ' + msg.payload.contact_name;
            if (config.callCenter?.notificationGrouping === true) {
              smsMessage = 'New SMS Message';
            }
            globals.$message({
              message: smsMessage,
              type: 'success',
              showClose: true,
              grouping: config.callCenter?.notificationGrouping ?? false,
            });
          }
        } else {
          // const userId = store.getters['auth/getLoginAccount'].userId;
          if (msg.payload.user_id == userId) {
            store.dispatch('realtime/modifyHaveNewSms', true);
          }

          const assign =
            msg.payload.user_id == userId ? 'assigned to' : 'unassigned from';
          if (
            config.callCenter.browser &&
            config.callCenter.browser.smsAssign === true
          ) {
            const title = 'Sms Assignment';
            _makeNotification(
              title,
              {
                body: `You have been ${assign} a SMS message`,
              },
              'sms',
              userId
            );
          }
          if (config.callCenter.smsNotifyAssign === true) {
            globals.$message({
              message: `Sms message ${assign} you`,
              type: 'success',
              showClose: true,
              grouping: config.callCenter?.notificationGrouping ?? false,
            });
          }
        }
        break;

      case 'airbnb':
        if (msg.payload.user_id == userId && !msg.payload.is_assignment) {
          // Ignore our own messages
          break;
        }
        // Alert components we have a new airbnb
        store.dispatch(
          'realtime/modifyNewAirbnbThreadId',
          msg.payload.contact_id
        );
        config = store.getters['appConfig/getAppConfig'];
        if (!config || !config.callCenter) {
          // If we cant tell just modify the badge and no notifcations
          store.dispatch('realtime/modifyHaveNewAirbnb', true);
          break;
        }

        if (msg.payload.user_id && msg.payload.is_assignment === false) {
          // Outbound non assignment messages we can skip for now.
          // Eventually a config based on direction too
          break;
        }

        // New airbnb badge takes takes precedence over assign
        if (msg.payload.is_assignment === false) {
          store.dispatch('realtime/modifyHaveNewAirbnb', true);
          // process notification types
          const longBody = `From: ${msg.payload.contact_name}

${msg.payload.content}
`;
          if (
            config.callCenter.browser &&
            config.callCenter.browser.airbnbNew
          ) {
            const payload = {
              body: longBody,
            };
            _makeNotification('New Airbnb Message', payload, 'airbnb', userId);
          }

          if (config.callCenter.airbnbNotifyNew === true) {
            let airbnbMessage = 'New Airbnb from ' + msg.payload.contact_name;
            if (config.callCenter?.notificationGrouping === true) {
              airbnbMessage = 'New Airbnb Message';
            }
            globals.$message({
              message: airbnbMessage,
              type: 'success',
              showClose: true,
              grouping: config.callCenter?.notificationGrouping ?? false,
            });
          }
        } else {
          const userId = store.getters['auth/getLoginAccount'].userId;
          if (msg.payload.user_id == userId) {
            store.dispatch('realtime/modifyHaveNewAirbnb', true);
          }

          const assign =
            msg.payload.user_id == userId ? 'assigned to' : 'unassigned from';
          if (
            config.callCenter.browser &&
            config.callCenter.browser.airbnbAssign === true
          ) {
            const title = 'Airbnb Assignment';
            _makeNotification(
              title,
              {
                body: `You have been ${assign} an Airbnb message`,
              },
              'airbnb',
              userId
            );
          }
          if (config.callCenter.airbnbNotifyAssign === true) {
            globals.$message({
              message: `Airbnb message ${assign} you`,
              type: 'success',
              showClose: true,
              grouping: config.callCenter?.notificationGrouping ?? false,
            });
          }
        }
        break;
      case 'email':
        if (msg.payload.user_id == userId && !msg.payload.is_assignment) {
          // Ignore our own messages
          break;
        }
        store.dispatch(
          'realtime/modifyNewMessageContactId',
          msg.payload.contact_id
        );
        config = store.getters['appConfig/getAppConfig'];
        if (!config || !config.callCenter) {
          store.dispatch('realtime/modifyHaveNewMessage', true);
          break;
        }
        // New email badge takes takes precedence over assign
        if (msg.payload.is_assignment === false) {
          store.dispatch('realtime/modifyHaveNewMessage', true);
          // process notification types
          const longBody = `From: ${msg.payload.contact_name}

${msg.payload.content}
`;
          if (config.callCenter.browser && config.callCenter.browser.emailNew) {
            const payload = {
              body: longBody,
            };
            _makeNotification('New Email', payload, 'email', userId);
          }

          if (config.callCenter.emailNotifyNew === true) {
            let emailMessage = 'New Email from ' + msg.payload.contact_name;
            if (config.callCenter?.notificationGrouping === true) {
              emailMessage = 'New Email Message';
            }
            globals.$message({
              message: emailMessage,
              type: 'success',
              showClose: true,
              grouping: config.callCenter?.notificationGrouping ?? false,
            });
          }
        } else {
          const userId = store.getters['auth/getLoginAccount'].userId;
          if (msg.payload.user_id == userId) {
            store.dispatch('realtime/modifyHaveNewMessage', true);
          }

          const assign =
            msg.payload.user_id == userId ? 'assigned to' : 'unassigned from';
          if (
            config.callCenter.browser &&
            config.callCenter.browser.emailAssign === true
          ) {
            const title = 'Email Assignment';
            _makeNotification(
              title,
              {
                body: `You have been ${assign} an Email`,
              },
              'email',
              userId
            );
          }
          if (config.callCenter.emailNotifyAssign === true) {
            globals.$message({
              message: `Email ${assign} you`,
              type: 'success',
              showClose: true,
              grouping: config.callCenter?.notificationGrouping ?? false,
            });
          }
        }
        break;
      case 'whatsapp':
        // Alert components we have a new whatsapp
        if (msg.payload.user_id == userId && !msg.payload.is_assignment) {
          // Ignore our own messages
          break;
        }
        store.dispatch(
          'realtime/modifyNewWhatsappContactId',
          msg.payload.contact_id
        );
        config = store.getters['appConfig/getAppConfig'];
        if (!config || !config.callCenter) {
          // If we cant tell just modify the badge and no notifcations
          store.dispatch('realtime/modifyHaveNewWhatsapp', true);
          break;
        }

        if (msg.payload.user_id && msg.payload.is_assignment === false) {
          // Outbound non assignment messages we can skip for now.
          // Eventually a config based on direction too
          break;
        }

        // New whatsapp badge takes takes precedence over assign
        if (msg.payload.is_assignment === false) {
          store.dispatch('realtime/modifyHaveNewWhatsapp', true);
          // process notification types
          const longBody = `From: ${msg.payload.contact_name}

${msg.payload.content}
`;
          if (
            config.callCenter.browser &&
            config.callCenter.browser.whatsappNew
          ) {
            const payload = {
              body: longBody,
            };
            _makeNotification(
              'New Whataspp Message',
              payload,
              'whatsapp',
              userId
            );
          }

          if (config.callCenter.whatsappNotifyNew === true) {
            let whatsappMessage =
              'New Whatsapp from ' + msg.payload.contact_name;
            if (config.callCenter?.notificationGrouping === true) {
              whatsappMessage = 'New Whatsapp Message';
            }
            globals.$message({
              message: whatsappMessage,
              type: 'success',
              showClose: true,
              grouping: config.callCenter?.notificationGrouping ?? false,
            });
          }
        } else {
          const userId = store.getters['auth/getLoginAccount'].userId;
          if (msg.payload.user_id == userId) {
            store.dispatch('realtime/modifyHaveNewWhatsapp', true);
          }

          const assign =
            msg.payload.user_id == userId ? 'assigned to' : 'unassigned from';
          if (
            config.callCenter.browser &&
            config.callCenter.browser.whatsappAssign === true
          ) {
            const title = 'Whatsapp Assignment';
            _makeNotification(
              title,
              {
                body: `You have been ${assign} a Whatsapp message`,
              },
              'whatsapp',
              userId
            );
          }
          if (config.callCenter.whatsappNotifyAssign === true) {
            globals.$message({
              message: `Whatsapp message ${assign} you`,
              type: 'success',
              showClose: true,
              grouping: config.callCenter?.notificationGrouping ?? false,
            });
          }
        }
        break;
      case 'task':
        if (msg.payload.user_id == userId && !msg.payload.is_assignment) {
          // Ignore our own messages
          break;
        }
        // For now we only do assign on task but we can do remind_at eventually
        // wrapped in here to fix hoisting of user_id as a const in a switch
        if (true || msg.payload.is_assignment === true) {
          config = store.getters['appConfig/getAppConfig'];
          if (!config || !config.callCenter) {
            // If we cant tell just modify the badge and no notifications
            store.dispatch('realtime/modifyHaveNewTask', true);
            break;
          }

          const userId = store.getters['auth/getLoginAccount'].userId;
          if (msg.payload.user_id == userId) {
            store.dispatch('realtime/modifyHaveNewTask', true);
          }

          const assign =
            msg.payload.user_id == userId ? 'assigned to' : 'unassigned from';
          if (
            config.callCenter.browser &&
            config.callCenter.browser.taskAssign === true
          ) {
            const title = 'Task Assignment';
            _makeNotification(
              title,
              {
                body: `You have been ${assign} a task`,
              },
              'task',
              userId
            );
          }
          if (config.callCenter.taskNotifyAssign === true) {
            globals.$message({
              message: `Task ${assign} you`,
              type: 'success',
              showClose: true,
              grouping: config.callCenter?.notificationGrouping ?? false,
            });
          }
        }
        break;
      case 'voice':
        config = store.getters['appConfig/getAppConfig'];
        if (!config || !config.callCenter) {
          // if no config just update call and carry on
          store.dispatch('realtime/modifyCall', msg.payload);
          break;
        }
        // Lots of cases to consider here.
        const oldCall = store.getters['realtime/getCallById'](
          msg.payload?.call_id
        );
        // Old call might be undefined if it hits the queue immediately
        // userId = store.getters['auth/getLoginAccount']?.userId;
        if (
          config.callCenter.browser &&
          config.callCenter.browser.incomingCall &&
          msg.payload.user_id &&
          msg.payload.user_id == userId &&
          oldCall.direction == 'inbound'
        ) {
          const title = 'New Incoming Call';
          const body = `Call from ${oldCall?.caller_name}`;
          _makeNotification(
            title,
            {
              body: body,
            },
            'incomingCall',
            userId
          );
        }
        if (
          msg.payload?.status == 'queue' &&
          msg.payload.queue_id &&
          msg.payload.queue_id != oldCall?.queue_id
        ) {
          // alert a call just went into queue
          if (config.callCenter.queueCallNotifyNew) {
            const queues = store.getters['queues/getQueues'];
            const queue = queues?.find((q) => q.id == msg.payload.queue_id);
            // Perhaps add a config for include all queues in alerts, perhaps
            if (
              queue &&
              queue.agents.find(
                (a) => a.id == store.getters['auth/getLoginAccount']?.userId
              )
            ) {
              const title = 'New Queued Call';
              const body = `Call from ${oldCall?.caller_name} entered queue ${queue?.name}`;
              _makeNotification(
                title,
                {
                  body: body,
                },
                'queueCall', // We play a unique audio for these alerts
                userId
              );
              globals.$message({
                message: body,
                type: 'success',
                showClose: true,
                duration: 10000, // Make this a config too
              });
              if (!queueCallAudio) {
                const url =
                  config?.callCenter?.queueCallAudio ?? QUEUE_CALL_URL;
                queueCallAudio = new Audio(url);
              }
              if (
                store.getters['realtime/getMyAnsweredSegments'](userId)
                  .length == 0
              ) {
                queueCallAudio.play();
              }
            }
          }
        }
        store.dispatch('realtime/modifyCall', msg.payload);
        break;
      case 'voiceSegment':
        store.dispatch('realtime/modifySegment', msg.payload);
        break;
      case 'voicemail':
        config = store.getters['appConfig/getAppConfig'];
        if (!config || !config.callCenter) {
          // We assume new are incomplete too
          store.dispatch('realtime/modifyHaveNewVoicemail', true);
          store.dispatch('realtime/modifyHaveIncompleteVoicemail', true);
          break;
        }

        // New email badge takes takes precedence over assign
        if (msg.payload.is_assignment === false) {
          store.dispatch('realtime/modifyHaveNewVoicemail', true);
          store.dispatch('realtime/modifyHaveIncompleteVoicemail', true);
          // process notification types
          if (config.callCenter.browser && config.callCenter.browser.vmNew) {
            const payload = {
              body: `From: ${msg.payload.contact_name}
              `,
            };
            _makeNotification('New Voicemail', payload, 'voicemail', userId);
          }

          if (config.callCenter.vmNotifyNew === true) {
            let voicemailMessage =
              'New Voicemail from: ' + msg.payload.contact_name;
            if (config.callCenter?.notificationGrouping === true) {
              voicemailMessage = 'New Voicemail';
            }
            globals.$message({
              message: voicemailMessage,
              type: 'success',
              showClose: true,
              grouping: config.callCenter?.notificationGrouping ?? false,
            });
          }
        } else {
          const userId = store.getters['auth/getLoginAccount'].userId;
          if (msg.payload.user_id == userId) {
            store.dispatch('realtime/modifyHaveNewVoicemail', true);
            store.dispatch('realtime/modifyHaveIncompleteVoicemail', true);
          }

          const assign =
            msg.payload.user_id == userId ? 'assigned to' : 'unassigned from';
          if (
            config.callCenter.browser &&
            config.callCenter.browser.vmAssign === true
          ) {
            const title = 'Voicemail Assignment';
            _makeNotification(
              title,
              {
                body: `You have been ${assign} a voicemail`,
              },
              'voicemail',
              userId
            );
          }
          if (config.callCenter.vmNotifyAssign === true) {
            globals.$message({
              message: `Voicemail ${assign} you`,
              type: 'success',
              showClose: true,
              grouping: config.callCenter?.notificationGrouping ?? false,
            });
          }
        }
        break;
      case 'notification':
        /* Test this is not needed anymore
        if (msg.payload.related_type == 'voicemail') {
          store.dispatch('realtime/modifyHaveNewVoicemail', true);
        }
        */
        // Stored in the inbox, so the bell badge goes up
        if (msg.payload.notification_id) {
          store.dispatch('realtime/adjustUnreadNotifications', 1);
        }
        if (
          msg.payload.related_type == 'conversation' &&
          msg.payload.related_id
        ) {
          // @mention on a conversation, click through to it
          _conversationAlert(msg.payload, userId);
          break;
        }
        globals.$message({
          message: msg.payload.message,
          type: 'success',
          showClose: true,
        });

        break;
    }
  },
};

// Clickable in app (and browser if allowed) alert that opens the conversation
const _conversationAlert = (payload, userId) => {
  const openConversation = () => {
    window.focus();
    router.push({
      path: '/admin/conversations/' + payload.related_id,
      // Scroll to the mentioned note
      query: payload.timeline_id
        ? { mentionTimeline: payload.timeline_id }
        : undefined,
    });
    // Opening it from the alert counts as reading it
    if (payload.notification_id) {
      userNotificationService
        .markRead(payload.notification_id)
        .then((updated) => {
          if (updated) {
            store.dispatch('realtime/adjustUnreadNotifications', -1);
          }
        })
        .catch((e) => console.log('Unable to mark notification read', e));
    }
  };

  let handle = undefined;
  handle = globals.$message({
    message: h(
      'span',
      {
        style: 'cursor: pointer',
        onClick: () => {
          handle?.close();
          openConversation();
        },
      },
      [h('strong', payload.subject), ': ' + payload.message]
    ),
    type: 'info',
    showClose: true,
    duration: 10000,
  });

  const config = store.getters['appConfig/getAppConfig'];
  if (
    'Notification' in window &&
    Notification.permission === 'granted' &&
    config?.callCenter?.browser?.mention !== false
  ) {
    _makeNotification(
      payload.subject,
      { body: payload.message },
      'mention',
      userId,
      openConversation
    );
  }
};

// payload will be the full options as provided
const _makeNotification = (title, payload, notifyType, userId, onClick) => {
  const thisConfig = store.getters['appConfig/getAppConfig'];

  let playAudio; // The sound we are going to play, if any
  switch (notifyType) {
    case 'airbnb':
      playAudio = _getOrCreateAudio(
        // May be empty, null, or a url
        thisConfig?.callCenter?.browser?.airbnbAudio,
        notifyType
      );
      break;
    case 'email':
      playAudio = _getOrCreateAudio(
        thisConfig?.callCenter?.browser?.emailAudio,
        notifyType
      );
      break;
    case 'incomingCall':
      // Plays audio from incoming call dialog only
      break;
    case 'lead':
      playAudio = _getOrCreateAudio(
        thisConfig?.callCenter?.browser?.leadAudio,
        notifyType
      );
      break;
    case 'mention':
      // Default sound unless configured, null turns it off
      playAudio = _getOrCreateAudio(
        thisConfig?.callCenter?.browser?.mentionAudio === undefined
          ? ''
          : thisConfig.callCenter.browser.mentionAudio,
        notifyType
      );
      break;
    case 'queueCall':
      // One off that is handled uniquely
      break;
    case 'sms':
      playAudio = _getOrCreateAudio(
        thisConfig?.callCenter?.browser?.smsAudio,
        notifyType
      );
      break;
    case 'task':
      playAudio = _getOrCreateAudio(
        thisConfig?.callCenter?.browser?.taskAudio,
        notifyType
      );
      break;
    case 'voicemail':
      playAudio = _getOrCreateAudio(
        thisConfig?.callCenter?.browser?.vmAudio,
        notifyType
      );
      break;
    case 'whatsapp':
      playAudio = _getOrCreateAudio(
        thisConfig?.callCenter?.browser?.whatsappAudio,
        notifyType
      );
      break;
    default:
      console.log('unexpected notify type; no sound playing:', notifyType);
  }

  // Eventually set timeout options and other good stuff
  payload['icon'] =
    'https://static.sendsquared.com/images/favicons/favicon.ico';
  const notification = new Notification(title, payload);
  if (
    playAudio &&
    store.getters['realtime/getMyAnsweredSegments'](userId).length == 0
  ) {
    playAudio.play();
  }
  notification.onclick = function (evt) {
    window.focus();
    if (onClick) {
      onClick();
    }
  };
  notification.onclose = function (evt) {
    // nothing to do for now
  };
};

const _getOrCreateAudio = (url, notifyType) => {
  // Url will be null, no audio
  // empty string, use default URL
  // explicit url to audio
  let playAudio = undefined; // Might be nothing
  // Explicit null means off
  if (url === null) {
    return playAudio;
  }
  // Explicit undefined is default
  if (url === '') {
    if (!notifyAudio) {
      notifyAudio = new Audio(NOTIFY_URL);
    }
    return notifyAudio;
  }

  // Else per type defined audio, create as needed
  if (NotificationSounds[notifyType] == undefined) {
    NotificationSounds[notifyType] = new Audio(url);
  }
  return NotificationSounds[notifyType];
};
